const FINGERPRINT_STORAGE_KEY = 'vt_last_fingerprint_capture';
const FINGERPRINT_VISITOR_ID_KEY = 'vt_fingerprint_visitor_id';

let fpAgentPromise = null;
let visitorResultCache = null;
let loginPayloadCache = null;
let fullCaptureCache = null;
let warmupPromise = null;

function parseUserAgent(ua) {
  const userAgent = ua || navigator.userAgent || '';
  let browser = 'Unknown';
  let os = 'Unknown';

  if (/Edg\//i.test(userAgent)) browser = 'Edge';
  else if (/Chrome\//i.test(userAgent) && !/Chg/i.test(userAgent)) browser = 'Chrome';
  else if (/Firefox\//i.test(userAgent)) browser = 'Firefox';
  else if (/Safari\//i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = 'Safari';
  else if (/OPR\//i.test(userAgent) || /Opera/i.test(userAgent)) browser = 'Opera';

  if (/Windows/i.test(userAgent)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(userAgent)) os = 'macOS';
  else if (/Android/i.test(userAgent)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(userAgent)) os = 'iOS';
  else if (/Linux/i.test(userAgent)) os = 'Linux';

  const deviceType = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)
    ? 'mobile'
    : 'desktop';

  const deviceLabel =
    deviceType === 'mobile'
      ? `Mobile - ${browser}`
      : `Desktop - ${browser}`;

  return { browser, os, deviceType, deviceLabel, userAgent };
}

function buildCaptureFromAgentResult(result, uaInfo, extra = {}) {
  return {
    visitorId: result?.visitorId ?? null,
    confidence: result?.confidence,
    components: result?.components ?? undefined,
    ...uaInfo,
    timings: extra.timings,
    capturedAt: extra.capturedAt || new Date().toISOString(),
    fromCache: extra.fromCache ?? false,
  };
}

function buildFallbackCapture(visitorId, confidence, uaInfo, extra = {}) {
  return buildCaptureFromAgentResult(
    { visitorId, confidence, components: extra.components },
    uaInfo,
    extra,
  );
}

function extractByDotNotation(obj, allowedPaths, currentPath='') {
  // 1. Handle arrays of objects
  if (Array.isArray(obj)) {
    return obj
      .map(item => extractByDotNotation(item, allowedPaths, currentPath))
      .filter(item => item !== null && (typeof item !== 'object' || Object.keys(item).length > 0));
  }

  // 2. Handle plain objects
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      // Construct the precise path for the current key
      const newPath = currentPath ? `${currentPath}.${key}` : key;

      // Check if this specific path or any subpath underneath it is allowed
      const isExactMatch = allowedPaths.includes(newPath);
      const isParentOfAllowedPath = allowedPaths.some(path => path.startsWith(newPath + '.'));

      if (isExactMatch) {
        // If it's a primitive or we want the whole sub-tree, copy it
        acc[key] = obj[key];
      } else if (isParentOfAllowedPath && typeof obj[key] === 'object' && obj[key] !== null) {
        // If it's an intermediate step down to an allowed key, dig deeper
        const deeperResult = extractByDotNotation(obj[key], allowedPaths, newPath);
        if (deeperResult !== null && Object.keys(deeperResult).length > 0) {
          acc[key] = deeperResult;
        }
      }
      
      return acc;
    }, {});
  }

  // 3. Return primitive values if they match, otherwise discard
  return allowedPaths.includes(currentPath) ? obj : null;
}


/** Full capture sent with login, including FingerprintJS components and timings. */
export function toLoginFingerprintPayload(capture) {
  if (!capture || typeof capture !== 'object') return null;
  const pathsToInclude = [
    "visitorId",
    "browser",
    "capturedAt",
    "deviceLabel", 
    "deviceType",
    "os",
    "userAgent",
    "fromCache",
    "components.cookiesEnabled.value",
    "components.dateTimeLocale.value",
    "components.deviceMemory.value",
    "components.fonts.value",
    "components.hardwareConcurrency.value",
    "components.languages.value",
    "components.localStorage.value",
    "components.osCpu.value",
    "components.platform.value",
    "components.screenResolution.value",
    "components.sessionStorage.value",
    "components.timezone.value",
    "components.touchSupport.value",
    "components.userAgentData.value",
    "components.vendor.value",
    "components.vendorFlavors.value",
  ];
  let respJson = extractByDotNotation(capture, pathsToInclude);
  return respJson;
}

function readPersistedCapture() {
  try {
    const raw = localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistCapture(capture) {
  try {
    localStorage.setItem(FINGERPRINT_STORAGE_KEY, JSON.stringify(capture));
  } catch (e) {
    console.warn('[fingerprint] could not persist capture', e);
  }
}

function cacheCaptures(capture) {
  fullCaptureCache = capture;
  loginPayloadCache = toLoginFingerprintPayload(capture);
  persistCapture(capture);
  return loginPayloadCache;
}

function loadFingerprintAgent() {
  if (!fpAgentPromise) {
    fpAgentPromise = import('@fingerprintjs/fingerprintjs').then((FingerprintJS) =>
      FingerprintJS.load(),
    );
  }
  return fpAgentPromise;
}

/** Full capture from memory or localStorage (may lack components until warmup finishes). */
export function getFullFingerprintCaptureSync() {
  if (fullCaptureCache) return fullCaptureCache;
  return readPersistedCapture();
}

/** Wait for collection and return the complete capture (components + timings). */
export async function ensureFullFingerprintCapture() {
  const sync = getFullFingerprintCaptureSync();
  if (sync?.components) {
    return sync;
  }
  await warmupFingerprint();
  const after = getFullFingerprintCaptureSync();
  if (after?.components) {
    return after;
  }
  return captureFingerprint();
}

/** Start fingerprint collection as soon as the app loads (non-blocking). */
export function warmupFingerprint() {
  if (loginPayloadCache) {
    return Promise.resolve(loginPayloadCache);
  }
  if (warmupPromise) {
    return warmupPromise;
  }
  warmupPromise = captureFingerprint()
    .then((capture) => cacheCaptures(capture))
    .then(() => loginPayloadCache)
    .catch((err) => {
      warmupPromise = null;
      console.warn('[fingerprint] warmup failed', err);
      const fallback = buildFallbackCapture(
        null,
        undefined,
        parseUserAgent(navigator.userAgent),
      );
      cacheCaptures(fallback);
      return loginPayloadCache;
    });
  return warmupPromise;
}

async function captureFingerprint() {
  const uaInfo = parseUserAgent(navigator.userAgent);
  const cachedVisitorId = sessionStorage.getItem(FINGERPRINT_VISITOR_ID_KEY);
  const lastPersisted = readPersistedCapture();

  if (
    cachedVisitorId &&
    lastPersisted?.visitorId === cachedVisitorId &&
    lastPersisted?.components
  ) {
    const capture = {
      ...lastPersisted,
      ...uaInfo,
      fromCache: true,
      capturedAt: lastPersisted.capturedAt || new Date().toISOString(),
    };
    visitorResultCache = {
      visitorId: capture.visitorId,
      confidence: capture.confidence,
      components: capture.components,
    };
    cacheCaptures(capture);
    return capture;
  }

  let visitorId = cachedVisitorId;
  let confidence;
  let components;
  let timings;

  try {
    const totalStart = performance.now();
    const loadStart = performance.now();
    const agent = await loadFingerprintAgent();
    const loadMs = performance.now() - loadStart;
    const getStart = performance.now();
    const result = await agent.get();
    const getMs = performance.now() - getStart;
    const totalMs = performance.now() - totalStart;

    visitorId = result.visitorId;
    confidence = result.confidence;
    components = result.components;
    timings = { loadMs, getMs, totalMs };
    sessionStorage.setItem(FINGERPRINT_VISITOR_ID_KEY, visitorId);
    visitorResultCache = { visitorId, confidence, components };
  } catch (err) {
    console.warn('[fingerprint] capture failed', err);
  }

  const capture = buildFallbackCapture(visitorId, confidence, uaInfo, {
    components,
    timings,
    fromCache: false,
  });
  cacheCaptures(capture);
  return capture;
}

/** Full capture prepared during warmup, including components and timings. */
export async function getLoginFingerprintPayload() {
  if (loginPayloadCache) {
    return loginPayloadCache;
  }
  return warmupFingerprint();
}
